import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { config } from '../constants/config';

// Import actions from voiceActorSlice
import {
  updateCache,
  clearActorUserErrors,
  clearPortfolioErrors,
  clearCharacterErrors,
  clearVariationErrors
} from '../slices/voiceActorSlice';

/**
 * Custom hook for fetching all Voice Lab data in a single request
 * This hook replaces the previous useVoiceLabData hook with a more efficient implementation
 * that fetches all data in a single request
 * 
 * @param {Object} options - Options for the hook
 * @param {string} options.actorId - Optional actor ID to filter data by
 * @param {boolean} options.forceRefresh - Force a refresh of the data even if it's already loaded
 * @returns {Object} Object containing Voice Lab data and loading states
 */
export const useVoiceLabCompositeData = (options = {}) => {
  const { actorId, forceRefresh = false } = options;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    actorUsers: [],
    portfolios: [],
    characters: [],
    variationsMap: {},
    // New flat arrays for direct access
    flatPortfolios: [],
    flatCharacters: [],
    flatVariations: [],
    isDataLoaded: false,
    loadingStates: {
      actorUsers: 'idle',
      portfolios: 'idle',
      characters: 'idle',
      variations: 'idle'
    }
  });
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Skip fetching if data is already loaded and forceRefresh is false
        if (data.isDataLoaded && !forceRefresh && lastFetchTime) {
          const timeSinceLastFetch = Date.now() - lastFetchTime;
          // Only refetch if it's been more than 5 minutes since the last fetch
          if (timeSinceLastFetch < 5 * 60 * 1000) {
            console.log('useVoiceLabCompositeData: Data already loaded, skipping fetch');
            setIsLoading(false);
            return;
          }
        }

        setIsLoading(true);
        setError(null);

        // Clear any previous errors in Redux
        dispatch(clearActorUserErrors());
        dispatch(clearPortfolioErrors());
        dispatch(clearCharacterErrors());
        dispatch(clearVariationErrors());

        console.log('useVoiceLabCompositeData: Fetching data...');

        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Build the URL with optional actorId filter
        let url = config.VOICE_LAB_COMPOSITE_DATA;
        if (actorId) {
          url += `?actorId=${encodeURIComponent(actorId)}`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch Voice Lab data: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('useVoiceLabCompositeData: Data received:', {
          actorUsers: result.actorUsers.length,
          portfolios: result.portfolios.length,
          characters: result.characters.length,
          variationsMap: Object.keys(result.variationsMap).length,
          // Log the new flat arrays if they exist
          flatPortfolios: result.flatPortfolios?.length || 0,
          flatCharacters: result.flatCharacters?.length || 0,
          flatVariations: result.flatVariations?.length || 0
        });

        // Log the nested structure for debugging
        if (result.actorUsers && result.actorUsers.length > 0) {
          console.log('useVoiceLabCompositeData: Nested structure detected:', {
            actorCount: result.actorUsers.length,
            portfoliosInActors: result.actorUsers.reduce((sum, actor) => sum + (actor.portfolios?.length || 0), 0),
            charactersInPortfolios: result.actorUsers.reduce((sum, actor) =>
              sum + actor.portfolios?.reduce((pSum, portfolio) =>
                pSum + (portfolio.characters?.length || 0), 0) || 0, 0)
          });
        }

        // Update the Redux store with the fetched data
        dispatch(updateCache({
          key: 'voiceLabCompositeData',
          data: result
        }));

        // Process the nested structure to create flat arrays if they don't exist
        const processedData = { ...result };

        // Create flat arrays if they don't exist in the API response
        if (!processedData.flatPortfolios && processedData.actorUsers) {
          // Create flat portfolios array
          processedData.flatPortfolios = [];
          processedData.actorUsers.forEach(actor => {
            if (actor.portfolios && Array.isArray(actor.portfolios)) {
              // Add actor ID to each portfolio for filtering
              const portfoliosWithActorId = actor.portfolios.map(portfolio => ({
                ...portfolio,
                actorId: actor.uuid // Add actorId for filtering
              }));
              processedData.flatPortfolios.push(...portfoliosWithActorId);
            }
          });

          // Create flat characters array
          processedData.flatCharacters = [];
          processedData.flatPortfolios.forEach(portfolio => {
            if (portfolio.characters && Array.isArray(portfolio.characters)) {
              // Add portfolio ID to each character for filtering
              const charactersWithPortfolioId = portfolio.characters.map(character => ({
                ...character,
                portfolioId: portfolio.uuid, // Add portfolioId for filtering
                actorId: portfolio.actorId // Add actorId for filtering
              }));
              processedData.flatCharacters.push(...charactersWithPortfolioId);
            }
          });

          // Create flat variations array
          processedData.flatVariations = [];
          processedData.flatCharacters.forEach(character => {
            if (character.variations && Array.isArray(character.variations)) {
              // Add character ID to each variation for filtering
              const variationsWithCharacterId = character.variations.map(variation => ({
                ...variation,
                characterId: character.uuid, // Add characterId for filtering
                portfolioId: character.portfolioId, // Add portfolioId for filtering
                actorId: character.actorId // Add actorId for filtering
              }));
              processedData.flatVariations.push(...variationsWithCharacterId);
            }
          });

          console.log('useVoiceLabCompositeData: Created flat arrays from nested structure:', {
            flatPortfolios: processedData.flatPortfolios.length,
            flatCharacters: processedData.flatCharacters.length,
            flatVariations: processedData.flatVariations.length
          });
        }

        // Update local state
        setData({
          ...processedData,
          loadingStates: {
            actorUsers: 'succeeded',
            portfolios: 'succeeded',
            characters: 'succeeded',
            variations: 'succeeded'
          }
        });

        setLastFetchTime(Date.now());
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading Voice Lab data:', err);
        setError(err.message || 'Failed to load Voice Lab data');
        setIsLoading(false);
      }
    };

    fetchData();
  }, [actorId, forceRefresh, data.isDataLoaded, lastFetchTime, dispatch]);

  return {
    ...data,
    isLoading,
    error
  };
};

export default useVoiceLabCompositeData;
